import styled from 'styled-components'
import { BuyAgainIcon } from '@shipt/design-system-icons'
import { Box, Stack } from '@shipt/design-system-layouts'
import { CarouselSection } from '@shipt/design-system-carousel'
import { useRenderShelfProducts } from '@/features/shop/components/Shelf/hooks/useRenderShelfProducts'
import { type Shelf as IShelf } from '@/features/shop/services/Shelves/types'
import { type Product } from '@/features/shop/services/Product/types'
import { type RenderQuickAdd } from '@/features/shop/components/ProductCard/types'
import { useSearchQueryParams } from '@/features/shared/hooks/search'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { screenSizes } from '@shipt/design-system-themes'

type Props = {
  shelf: IShelf<Product>
  shelfIndex: number
  renderQuickAdd?: RenderQuickAdd
}

export const BuyAgainProductShelf = ({ shelf, shelfIndex, renderQuickAdd }: Props) => {
  const { query, category_id } = useSearchQueryParams()
  const searchParams = String(query ?? '')

  const shelfProducts = useRenderShelfProducts({
    products: shelf.items,
    shelfName: shelf.name,
    shelfIndex,
    renderQuickAdd,
    hideRatings: true,
    hasBackground: true,
    hideSecondaryDetails: true,
    linkToUniversalPDP: false,
    trackingProps: {
      searchTerm: searchParams,
      searchType: (() => {
        if (!query && !category_id) return 'none'
        if (!query && category_id) return 'browse'
        if (query && !category_id) return 'search'
        return 'none'
      })(),
    },
  })

  const onNextClick = () => {
    trackElementClicked({
      type: 'right_arrow',
      shelf_name: shelf.name,
      shelf_index: shelfIndex,
    })
  }

  const onPrevClick = () => {
    trackElementClicked({
      type: 'left_arrow',
      shelf_name: shelf.name,
      shelf_index: shelfIndex,
    })
  }

  return (
    <ScrollIntoView
      onInView={() => {
        trackElementViewed({ shelf_index: shelfIndex, shelf_name: shelf.name })
      }}
    >
      <ShelfWrapper padding={['lg', 0]}>
        <CarouselSection hasInlineControls>
          <Header
            leading={
              <Stack>
                <BuyAgainIcon />
              </Stack>
            }
            title="Buy Again"
          />
          <CarouselSection.Carousel
            layoutConfig={{
              0: {
                // @ts-expect-error is valid attribute accepted by swiper but not currently typed as such in Astro.
                slidesPerView: 'auto',
                slidesPerGroup: 1,
                slidesPerGroupAuto: true,
              },
            }}
            onNextClick={onNextClick}
            onPrevClick={onPrevClick}
          >
            {shelfProducts}
          </CarouselSection.Carousel>
        </CarouselSection>
      </ShelfWrapper>
    </ScrollIntoView>
  )
}

const ShelfWrapper = styled(Box)`
  /* this variable is used to set the gradient color behind the nav buttons inside the Carousel */
  --carousel-container-bg: ${({ theme }) => theme.color.background.base.neutral.subtle_100};
  background-color: var(--carousel-container-bg);

  @media ${screenSizes.tablet} {
    border-radius: 0.5rem;
  }

  .swiper-slide {
    width: auto;
  }
`

const Header = styled(CarouselSection.Header)`
  @media ${screenSizes.tablet} {
    padding: 0 1rem;
  }
`
